import type {
  TrainingScheduleOperationsItem,
  UpdateTrainingScheduleReq,
} from "@/api/trainingSchedule";
import {
  datetimeLocalToUtcIso,
  isBranchRef,
  minFreeGpusInvalidMessage,
  resolveScheduleTimezone,
  SCHEDULE_END_IN_PAST_MESSAGE,
  SCHEDULE_END_BEFORE_START_MESSAGE,
  scheduleIntervalSeconds,
  scheduleMinFreeGpus,
  utcIsoToDatetimeLocal,
  type ScheduleDraft,
  type ScheduleRecurrenceMode,
} from "@/page/training/submit/scheduleDraft";
import { validateCronExpression } from "@/modules/training/public";
import { parseUtcMs } from "@/utils/datetime";

/**
 * Schedule 편집 초안. 행의 현재 값을 시드로 넣고 **바뀐 필드만** PATCH 로 만든다.
 *
 * PATCH 는 보낸 필드만 적용한다(BE `exclude_unset`). 바뀌지 않은 값을 함께 보내면 그 사이 다른
 * 곳에서 바꾼 값을 되돌린다. `min_free_gpus` 는 3-상태라 부주의한 null 이 게이트를 지운다. 그래서
 * wire 로 무엇을 보낼지는 이 모듈의 `buildSchedulePatch` 한 곳이 소유한다(§16).
 *
 * 반복·기간·게이트 입력을 읽는 규칙은 생성 초안(`scheduleDraft`)의 규칙을 그대로 쓴다. 같은 축을
 * 생성과 편집이 다르게 읽으면 두 화면의 판정이 달라진다.
 */

/**
 * 실행 게이트를 켤 수 있는지와, 켤 수 없다면 그 사유.
 *
 * 켤 수 없다는 결과가 같아도 **사유가 다르면 사용자가 할 수 있는 일이 다르다**(§16). 추종 축에는
 * 뜻이 다른 상태가 둘 있다. 둘을 합치면 화면이 사실이 아닌 내용을 표시한다.
 *
 * - `learning_core` null: 상류는 `recipe_json` 에 learning-core dict 가 없으면 null 을 보낸다(v1
 *   스키마 행이거나 처음부터 고정하지 않은 recipe). 추종할 Branch 가 없는 상태이고, Commit 을
 *   고정한 상태가 아니다.
 * - `ref` 가 40자 hex: 이때가 Commit 고정이다.
 *
 * `lockedReason` 은 켤 수 있으면 빈 문자열이다. `GatedToggle` 은 잠겼을 때만 이 값을 읽는다.
 */
export type ScheduleGate = { available: boolean; lockedReason: string };

const AVAILABLE: ScheduleGate = { available: true, lockedReason: "" };

// 새 Commit 이 있을 때만 실행. recipe 의 learning-core 가 branch 를 가리킬 때만 켤 수 있다.
// 생성 스텝과 같은 축이지만 판정 재료가 다르다(폼의 branchRef vs 행의 recipe 요약).
export function branchFollowGate(item: TrainingScheduleOperationsItem): ScheduleGate {
  const ref = item.schedule_recipe.learning_core?.ref ?? "";
  if (ref.trim() === "") {
    return {
      available: false,
      lockedReason: "이 Schedule의 Recipe에는 learning-core 지정이 없어 추종할 Branch가 없습니다.",
    };
  }
  if (!isBranchRef(ref)) {
    return {
      available: false,
      lockedReason: "Commit을 고정한 Recipe는 바뀔 것이 없어 켤 수 없습니다.",
    };
  }
  return AVAILABLE;
}

// 최신 Release 사용은 Evaluation 이 붙은 recipe 를 전제로 한다(아니면 상류가 422 를 반환한다).
export function trackLatestEvalGate(item: TrainingScheduleOperationsItem): ScheduleGate {
  if (!item.schedule_recipe.has_eval) {
    return {
      available: false,
      lockedReason: "이 Schedule의 Recipe에는 Evaluation이 없어 바꿀 Image가 없습니다.",
    };
  }
  return AVAILABLE;
}

export type ScheduleEditDraft = {
  name: string;
  description: string;
  mode: ScheduleRecurrenceMode;
  intervalMinutes: string;
  cron: string;
  timezone: string;
  skipIfUnchanged: boolean;
  // "" = 게이트 없음(행에 게이트가 있었다면 해제 → 명시 null).
  minFreeGpus: string;
  trackLatestEvalRelease: boolean;
  // 로컬 datetime-local 값. 상류 PATCH 는 값 변경은 받지만 null 은 무시한다. 한 번 정한 종료
  // 시각은 바꿀 수는 있지만 비울 수는 없다(빈 초안은 invalid).
  endAt: string;
};

/**
 * 행 → 초안 시드. "바뀌었는가"의 기준은 이 시드 문자열이다(`buildSchedulePatch` 가 초안을 이
 * 값과 비교한다). 그래서 왕복하면 달라지는 값도 바꾸지 않았으면 변경으로 읽지 않는다. 예: 60 으로
 * 나누어지지 않는 간격("5.5"), 초가 잘린 종료 시각.
 */
export function editDraftFromItem(item: TrainingScheduleOperationsItem): ScheduleEditDraft {
  const isCron = item.cron != null && item.cron !== "";
  return {
    name: item.name,
    description: item.description ?? "",
    mode: isCron ? "cron" : "interval",
    intervalMinutes: item.interval_seconds == null ? "" : String(item.interval_seconds / 60),
    cron: item.cron ?? "",
    // 상류는 생성 때 받은 timezone 을 그대로 저장한다(없으면 null = UTC 로 실행). null 을 빈 값으로
    // 시드하면 기본값(Asia/Seoul)으로 읽는다. 그러면 설명만 고쳐도 시간대가 알림 없이 바뀐다. 그래서 UTC 로 시드한다.
    timezone: item.timezone ?? (isCron ? "UTC" : ""),
    skipIfUnchanged: item.skip_if_unchanged,
    minFreeGpus: item.min_free_gpus == null ? "" : String(item.min_free_gpus),
    trackLatestEvalRelease: item.track_latest_eval_release,
    endAt: utcIsoToDatetimeLocal(item.end_at),
  };
}

// 편집 초안을 생성 초안 판독기·공용 필드와 같은 형태로 연결하는 최소 변환(반복·조건 축만 쓴다).
export function asScheduleDraft(draft: ScheduleEditDraft): ScheduleDraft {
  return {
    name: draft.name,
    description: draft.description,
    mode: draft.mode,
    intervalMinutes: draft.intervalMinutes,
    cron: draft.cron,
    timezone: draft.timezone,
    startAt: "",
    endAt: draft.endAt,
    skipIfUnchanged: draft.skipIfUnchanged,
    minFreeGpus: draft.minFreeGpus,
    trackLatestEvalRelease: draft.trackLatestEvalRelease,
  };
}

export type SchedulePatchResult =
  | { kind: "patch"; patch: UpdateTrainingScheduleReq; recurrenceChanged: boolean }
  | { kind: "unchanged" }
  | { kind: "invalid"; message: string };

// 반복 축이 행과 달라졌는지 판정한다. 바뀐 축만 보내야 상류 XOR 재계산이 정확히 그 축으로 실행된다.
// 비교 대상은 시드 문자열이다. 반복 방식을 바꾸면 값이 비어 있어도 변경이다. 그래서 빈 간격이
// "변경 없음"으로 알림 없이 닫히지 않는다. 바꾸지 않은 값은 왕복하면 달라져도 변경이 아니다.
function recurrenceChanged(seeded: ScheduleEditDraft, draft: ScheduleEditDraft): boolean {
  if (draft.mode !== seeded.mode) {
    return true;
  }
  if (draft.mode === "interval") {
    return draft.intervalMinutes.trim() !== seeded.intervalMinutes;
  }
  return (
    draft.cron.trim() !== seeded.cron ||
    resolveScheduleTimezone(draft.timezone) !== resolveScheduleTimezone(seeded.timezone)
  );
}

/**
 * 초안과 행을 비교해 바뀐 필드만 담은 PATCH 를 만든다.
 *
 * 반환은 세 가지 경우다:
 * - `unchanged`: 보낼 것이 없다. 요청을 보내지 않는다.
 * - `invalid`: 입력이 잘못되었다. 문구는 그 자리에서 표시한다.
 * - `patch`: 그 외. `recurrenceChanged` 는 저장 확인 문구(next_run_at 리셋 경고)의 근거다.
 */
export type BuildSchedulePatchOptions = {
  // 클러스터 GPU 총 수. 여유 GPU 조건의 실제 상한이다. 읽지 못했으면 null 이다(상류 폭 1024).
  totalGpus: number | null;
  // 지난 종료 시각을 판정하는 기준 시각. 테스트가 고정한다.
  nowMs?: number;
};

export function buildSchedulePatch(
  item: TrainingScheduleOperationsItem,
  draft: ScheduleEditDraft,
  { totalGpus = null, nowMs = Date.now() }: Partial<BuildSchedulePatchOptions> = {},
): SchedulePatchResult {
  const options = { totalGpus, nowMs };
  const patch: UpdateTrainingScheduleReq = {};
  const name = draft.name.trim();
  if (name === "") {
    return { kind: "invalid", message: "Schedule 이름을 입력해 주세요." };
  }
  if (name !== item.name) {
    patch.name = name;
  }
  const description = draft.description.trim();
  if (description !== (item.description ?? "")) {
    patch.description = description;
  }

  const seeded = editDraftFromItem(item);
  const recurrenceDirty = recurrenceChanged(seeded, draft);
  if (recurrenceDirty) {
    if (draft.mode === "interval") {
      const seconds = scheduleIntervalSeconds(asScheduleDraft(draft));
      if (seconds === null) {
        return { kind: "invalid", message: "반복 간격을 5분 이상의 정수로 입력해 주세요." };
      }
      patch.intervalSeconds = seconds;
    } else {
      const cron = draft.cron.trim();
      if (cron === "") {
        return { kind: "invalid", message: "cron 표현식을 입력해 주세요." };
      }
      const cronValidation = validateCronExpression(cron);
      if (cronValidation.kind === "invalid") {
        return { kind: "invalid", message: cronValidation.message };
      }
      patch.cron = cron;
      patch.timezone = resolveScheduleTimezone(draft.timezone);
    }
  }

  if (draft.skipIfUnchanged !== item.skip_if_unchanged) {
    patch.skipIfUnchanged = draft.skipIfUnchanged;
  }
  if (draft.trackLatestEvalRelease !== item.track_latest_eval_release) {
    patch.trackLatestEvalRelease = draft.trackLatestEvalRelease;
  }

  // 바꾸지 않은 조건은 검사하지 않는다. 클러스터가 줄어 저장된 조건이 지금 총 수를 넘어도, 이름만
  // 고치는 저장을 막으면 안 된다. 상한은 사용자가 값을 바꿀 때만 확인한다.
  if (draft.minFreeGpus.trim() !== seeded.minFreeGpus) {
    const minFreeGpus = scheduleMinFreeGpus(asScheduleDraft(draft), options.totalGpus);
    if (minFreeGpus.kind === "invalid") {
      return { kind: "invalid", message: minFreeGpusInvalidMessage(options.totalGpus) };
    }
    if (minFreeGpus.kind === "unset") {
      // 비우는 것이 항상 해제는 아니다. 행에 게이트가 있었을 때만 명시 null 로 지운다(§16 3-상태).
      if (item.min_free_gpus != null) {
        patch.minFreeGpus = null;
      }
    } else if (minFreeGpus.value !== (item.min_free_gpus ?? null)) {
      patch.minFreeGpus = minFreeGpus.value;
    }
  }

  const endAtDraft = draft.endAt.trim();
  if (endAtDraft === "") {
    if (item.end_at != null) {
      return {
        kind: "invalid",
        message: "종료 시각은 비울 수 없습니다. 바꾸려면 새 시각을 입력해 주세요.",
      };
    }
  } else if (endAtDraft !== seeded.endAt) {
    // 시드(분 단위)와 같으면 보내지 않는다. 초가 잘린 왕복 때문에 바꾸지 않은 저장이 end_at 을
    // 59초 앞당겨 다시 보내지 않게 한다. 바꾼 값은 현재 시각과 저장된 시작 시각보다 뒤여야 한다.
    const endAt = datetimeLocalToUtcIso(endAtDraft);
    if (endAt === null) {
      return { kind: "invalid", message: "종료 시각을 다시 확인해 주세요." };
    }
    const endMs = parseUtcMs(endAt) ?? 0;
    if (endMs <= options.nowMs) {
      return { kind: "invalid", message: SCHEDULE_END_IN_PAST_MESSAGE };
    }
    const startMs = parseUtcMs(item.start_at ?? null);
    if (startMs !== null && endMs <= startMs) {
      return { kind: "invalid", message: SCHEDULE_END_BEFORE_START_MESSAGE };
    }
    patch.endAt = endAt;
  }

  return Object.keys(patch).length === 0
    ? { kind: "unchanged" }
    : { kind: "patch", patch, recurrenceChanged: recurrenceDirty };
}
